import { isFlaggedEntity } from './hierarchyUtils'
import { findDegenerateLensPairs } from '../components/InvestigationReport/components/PeerEvidence/PeerEvidence'

/**
 * computeReadiness — the publish-readiness signals for a whole-run report
 * (target-state design §5.3a). Composes signals that already existed but were
 * scattered: engine-vs-derived count reconciliation (audit F7), never-probed
 * entities (F6), and degenerate peer lenses (F4). One surface, one checklist.
 *
 * @param {Object} report - the run report payload
 * @param {Array} entitySummaries - report.entitySummaries (or the view's list)
 * @returns {{
 *   derived: { scanned: number, flagged: number },
 *   authoritative: { scanned: ?number, flagged: ?number } | null,
 *   countMismatch: boolean,
 *   unprobedCount: number,
 *   lensPairs: Array<[string, string]>,
 * }}
 */
export const computeReadiness = (report, entitySummaries = []) => {
    const derived = {
        scanned: entitySummaries.length,
        flagged: entitySummaries.filter(isFlaggedEntity).length,
    }

    const statistics = report?.statistics
    const authoritative =
        statistics && (statistics.totalEntities != null || statistics.flaggedEntities != null)
            ? {
                  scanned: statistics.totalEntities ?? null,
                  flagged: statistics.flaggedEntities ?? null,
              }
            : null

    const countMismatch = Boolean(
        authoritative &&
            ((authoritative.scanned != null && authoritative.scanned !== derived.scanned) ||
                (authoritative.flagged != null && authoritative.flagged !== derived.flagged))
    )

    const unprobedCount = entitySummaries.filter((entity) => entity.unprobed === true).length

    // Degenerate lenses across the whole run, de-duplicated by pair.
    const seen = new Set()
    const lensPairs = []
    for (const entity of entitySummaries) {
        for (const pair of findDegenerateLensPairs(entity.peerEvidence)) {
            const key = pair.join('|')
            if (!seen.has(key)) {
                seen.add(key)
                lensPairs.push(pair)
            }
        }
    }

    return { derived, authoritative, countMismatch, unprobedCount, lensPairs }
}
